import { normalizeVisualStyleKey } from "./visualStyleReferences";
import { VIDEO_PROVIDER_VOLCENGINE_SEEDANCE, DOMESTIC_SEEDANCE_MODELS, getDomesticSeedanceResolutions, isDomesticOfficialSeedance } from './domesticSeedanceCapabilities';
export { VIDEO_PROVIDER_VOLCENGINE_SEEDANCE };
import {
  DEFAULT_SEEDANCE25_PROVIDER,
  SEEDANCE25_MODEL,
  VIDEO_PROVIDER_AKOOL_SD25,
  VIDEO_PROVIDER_ASTROFLOW_SD25,
  VIDEO_PROVIDER_BYTEPLUS_SD25,
  VIDEO_PROVIDER_VOLCENGINE_SD25,
  VIDEO_PROVIDER_BYTEPLUS_LAS_SD25,
  VIDEO_PROVIDER_NEWAPI_SD25 as CATALOG_NEWAPI_SD25,
  isSeedance25Provider,
  normalizeSeedance25Provider,
  normalizeSeedance25SubmissionProvider,
} from "./seedance25Capabilities";
import {
  WANX_VIDEO_MODEL,
  WANX_VIDEO_PRIME_MODEL,
  WANX_VIDEO_PROVIDER,
} from "./wanxVideoCapabilities";

export const PROJECT_MARKET_DOMESTIC = "domestic";
export const PROJECT_MARKET_OVERSEAS = "overseas";
export const PROJECT_PRODUCTION_MODE_STANDARD = "standard";
export const PROJECT_PRODUCTION_MODE_NARRATION = "narration";
export const PROJECT_PRODUCTION_MODE_REPAINT = "repaint";
export const PROJECT_PRODUCTION_MODE_INTERACTIVE_GAME = "interactive_game";
export const PROJECT_PRODUCTION_MODE_SD25 = "sd25";
export const PROJECT_PRODUCTION_MODE_WAN3 = "wan3";
export const VIDEO_PROVIDER_GUAVIDEO = "guavideo";
export const VIDEO_PROVIDER_ENG_GUAVIDEO = "engguavideo";
export const VIDEO_PROVIDER_OMINI_SEEDANCE = "omini_seedance";
export const VIDEO_PROVIDER_WANX = WANX_VIDEO_PROVIDER;
export const VIDEO_PROVIDER_WAND_VEGA = "wand_vega";
export const VIDEO_MODEL_WAN3 = WANX_VIDEO_MODEL;
export const VIDEO_MODEL_WAN3_PRIME = WANX_VIDEO_PRIME_MODEL;
export const VIDEO_PROVIDER_NEWAPI_SD25 = CATALOG_NEWAPI_SD25;
export { VIDEO_PROVIDER_AKOOL_SD25, VIDEO_PROVIDER_ASTROFLOW_SD25, VIDEO_PROVIDER_BYTEPLUS_SD25, VIDEO_PROVIDER_VOLCENGINE_SD25, VIDEO_PROVIDER_BYTEPLUS_LAS_SD25 };
export const VIDEO_PROVIDER_DEFAULT_SD25 = DEFAULT_SEEDANCE25_PROVIDER;
export const VIDEO_MODEL_NEWAPI_SD25 = SEEDANCE25_MODEL;
export const VIDEO_MODEL_SD25 = SEEDANCE25_MODEL;
export const VIDEO_POLLING_MAX_MS = 120 * 60 * 1000;
export const PROJECT_VISUAL_STYLE_FIXED = "fixed";
export const PROJECT_VISUAL_STYLE_NONE = "none";
export const PROJECT_VISUAL_STYLE_LEGACY = "legacy";
export const PROJECT_DEFAULT_VIDEO_RATIO = "9:16";
export const PROJECT_VIDEO_RATIOS = ["9:16", "16:9", "21:9"];
const PROJECT_PRODUCTION_MODE_HINT_PREFIX = "project_production_mode_hint_";
const PROJECT_PRODUCTION_MODES = new Set([
  PROJECT_PRODUCTION_MODE_STANDARD,
  PROJECT_PRODUCTION_MODE_NARRATION,
  PROJECT_PRODUCTION_MODE_REPAINT,
  PROJECT_PRODUCTION_MODE_INTERACTIVE_GAME,
  PROJECT_PRODUCTION_MODE_SD25,
  PROJECT_PRODUCTION_MODE_WAN3,
]);

const normalizeProjectProductionModeValue = (rawValue) => {
  const mode = String(rawValue || "").trim().toLowerCase();
  if (["narration", "narration_project", "narration-project", "解说剧", "解说剧生产"].includes(mode)) {
    return PROJECT_PRODUCTION_MODE_NARRATION;
  }
  if (["repaint", "video_repaint", "video-repaint", "影视转绘"].includes(mode)) {
    return PROJECT_PRODUCTION_MODE_REPAINT;
  }
  if ([
    "interactive_game",
    "interactive-game",
    "interactive_story",
    "interactive-story",
    "interactive",
    "互动游戏",
    "互动剧情",
    "互动小说",
  ].includes(mode)) {
    return PROJECT_PRODUCTION_MODE_INTERACTIVE_GAME;
  }
  if ([
    "sd25",
    "sd-2.5",
    "sd_2_5",
    "newapi_sd25",
    "akool_sd25",
    "astroflow_sd25",
    "byteplus_sd25",
    "volcengine_sd25",
    "byteplus_las_sd25",
    "即梦2.5",
    "即梦 2.5",
    "2.5项目",
  ].includes(mode)) {
    return PROJECT_PRODUCTION_MODE_SD25;
  }
  if ([
    "wan3",
    "wan3.0",
    "wan_3_0",
    "wan3.0-video",
    "wan3.0-video-prime",
    "wanx",
    "万相3.0",
    "万相 3.0",
    "万相3.0项目",
    "万相 3.0 项目",
  ].includes(mode)) {
    return PROJECT_PRODUCTION_MODE_WAN3;
  }
  return PROJECT_PRODUCTION_MODE_STANDARD;
};

export const getProjectProductionModeHint = (projectId) => {
  if (!projectId || typeof localStorage === "undefined") return "";
  try {
    const mode = String(localStorage.getItem(`${PROJECT_PRODUCTION_MODE_HINT_PREFIX}${projectId}`) || "").trim();
    return PROJECT_PRODUCTION_MODES.has(mode) ? mode : "";
  } catch {
    return "";
  }
};

export const rememberProjectProductionMode = (projectId, productionMode) => {
  if (!projectId || typeof localStorage === "undefined") return;
  try {
    const normalized = normalizeProjectProductionModeValue(productionMode);
    localStorage.setItem(`${PROJECT_PRODUCTION_MODE_HINT_PREFIX}${projectId}`, normalized);
  } catch {
    // 本地存储不可用时，仍以服务端 productionMode 为准。
  }
};

export const normalizeProjectProductionMode = (value) => {
  const project = typeof value === "object" && value !== null ? value : null;
  const normalized = normalizeProjectProductionModeValue(project ? project.productionMode : value);
  // 服务端明确返回 standard 时必须以服务端为准；hint 只补偿旧接口没有返回
  // productionMode 的短暂窗口，不能让一次创建时写入的旧 hint 永久覆盖项目类型。
  if (normalized !== PROJECT_PRODUCTION_MODE_STANDARD || !project?.id || String(project.productionMode || "").trim()) {
    return normalized;
  }
  return getProjectProductionModeHint(project.id) || normalized;
};

export const isNarrationProject = (project) => (
  normalizeProjectProductionMode(project) === PROJECT_PRODUCTION_MODE_NARRATION
);

export const isRepaintProject = (project) => (
  normalizeProjectProductionMode(project) === PROJECT_PRODUCTION_MODE_REPAINT
);

export const isInteractiveGameProject = (project) => (
  normalizeProjectProductionMode(project) === PROJECT_PRODUCTION_MODE_INTERACTIVE_GAME
);

export const isSd25Project = (projectOrId) => {
  if (typeof projectOrId === "object" && projectOrId !== null) {
    if (isSeedance25Provider(projectOrId.videoProvider)) {
      return true;
    }
    return normalizeProjectProductionMode(projectOrId) === PROJECT_PRODUCTION_MODE_SD25;
  }
  const project = getProjectRuntimeProfile(projectOrId);
  if (project) {
    if (isSeedance25Provider(project.videoProvider)) return true;
    return normalizeProjectProductionMode(project) === PROJECT_PRODUCTION_MODE_SD25;
  }
  return getProjectProductionModeHint(projectOrId) === PROJECT_PRODUCTION_MODE_SD25;
};

export const isWan3Project = (projectOrId) => {
  if (typeof projectOrId === "object" && projectOrId !== null) {
    if (normalizeVideoProvider(projectOrId.videoProvider) === VIDEO_PROVIDER_WANX) {
      return true;
    }
    return normalizeProjectProductionMode(projectOrId) === PROJECT_PRODUCTION_MODE_WAN3;
  }
  const project = getProjectRuntimeProfile(projectOrId);
  if (project) {
    if (normalizeVideoProvider(project.videoProvider) === VIDEO_PROVIDER_WANX) return true;
    return normalizeProjectProductionMode(project) === PROJECT_PRODUCTION_MODE_WAN3;
  }
  return getProjectProductionModeHint(projectOrId) === PROJECT_PRODUCTION_MODE_WAN3;
};

export const getProjectEntryPath = (project, productionModeOverride = null) => {
  if (!project?.id) return "/";
  const projectForNavigation = productionModeOverride
    ? { ...project, productionMode: productionModeOverride }
    : project;
  if (isRepaintProject(projectForNavigation)) return `/project/${project.id}/video-repaint`;
  if (isNarrationProject(projectForNavigation)) return `/project/${project.id}/narration-production`;
  if (isInteractiveGameProject(projectForNavigation)) return `/project/${project.id}/interactive-story`;
  return `/project/${project.id}`;
};

export const getProjectRuntimeProfile = (projectId) => {
  if (!projectId) return null;
  try {
    const raw = localStorage.getItem(`project_runtime_profile_${projectId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const isOverseasProjectProfile = (profile) => (
  profile?.projectMarket === PROJECT_MARKET_OVERSEAS
  || profile?.contentLanguage === "en-US"
  || profile?.videoProvider === VIDEO_PROVIDER_ENG_GUAVIDEO
  || profile?.videoProvider === VIDEO_PROVIDER_OMINI_SEEDANCE
  || normalizeSeedance25Provider(profile?.videoProvider) === VIDEO_PROVIDER_BYTEPLUS_SD25
  || normalizeSeedance25Provider(profile?.videoProvider) === VIDEO_PROVIDER_BYTEPLUS_LAS_SD25
);

export const normalizeVideoProvider = (value) => {
  const provider = String(value || "").trim().toLowerCase();
  if (isDomesticOfficialSeedance(provider)) return VIDEO_PROVIDER_VOLCENGINE_SEEDANCE;
  if ([VIDEO_PROVIDER_WANX, VIDEO_MODEL_WAN3, VIDEO_MODEL_WAN3_PRIME].includes(provider)) {
    return VIDEO_PROVIDER_WANX;
  }
  if (provider === VIDEO_PROVIDER_WAND_VEGA) return VIDEO_PROVIDER_WAND_VEGA;
  const seedance25Provider = normalizeSeedance25Provider(provider);
  if (seedance25Provider) return seedance25Provider;
  // Backward compatibility only: these ambiguous legacy aliases existed
  // before vendor identity was persisted and therefore mean New API.
  if ([VIDEO_PROVIDER_NEWAPI_SD25, "newapi-sd25", "sd25", "sd-2.5"].includes(provider)) {
    return VIDEO_PROVIDER_NEWAPI_SD25;
  }
  if ([VIDEO_PROVIDER_OMINI_SEEDANCE, "omini-seedance", "ominiseedance"].includes(provider)) {
    return VIDEO_PROVIDER_OMINI_SEEDANCE;
  }
  if (provider === VIDEO_PROVIDER_ENG_GUAVIDEO) return VIDEO_PROVIDER_ENG_GUAVIDEO;
  if (provider === VIDEO_PROVIDER_GUAVIDEO) return VIDEO_PROVIDER_GUAVIDEO;
  return "";
};

export const getProjectVideoProvider = (projectId, fallback = "") => {
  const profile = getProjectRuntimeProfile(projectId);
  // A routed project without a loaded runtime profile has no trustworthy vendor
  // identity yet. Fail closed instead of silently sending paid work/material IDs
  // to the historical domestic default.
  if (projectId && !profile) return "";
  const rawPersistedProvider = String(profile?.videoProvider || "").trim();
  const persistedProvider = normalizeVideoProvider(profile?.videoProvider);
  // A project with an explicitly persisted but unknown provider must not be
  // routed into another provider's material library.
  if (rawPersistedProvider && !persistedProvider) return "";
  if (isSeedance25Provider(persistedProvider)) return persistedProvider;
  if (isDomesticOfficialSeedance(persistedProvider)) return persistedProvider;
  // Old 2.5 projects predate explicit vendor persistence and belonged to New API.
  if (!rawPersistedProvider && isSd25Project(profile)) return VIDEO_PROVIDER_NEWAPI_SD25;
  if (persistedProvider === VIDEO_PROVIDER_OMINI_SEEDANCE) return VIDEO_PROVIDER_OMINI_SEEDANCE;
  if (isOverseasProjectProfile(profile)) return VIDEO_PROVIDER_ENG_GUAVIDEO;
  return persistedProvider || (projectId ? "" : normalizeVideoProvider(fallback));
};

export const getProjectVideoSubmissionProvider = (projectId, fallback = "") => {
  const provider = getProjectVideoProvider(projectId, fallback);
  return normalizeSeedance25SubmissionProvider(provider) || provider;
};

export const getVideoAssetApiBase = (provider) => (
  [VIDEO_PROVIDER_OMINI_SEEDANCE, VIDEO_PROVIDER_ENG_GUAVIDEO].includes(normalizeVideoProvider(provider))
    ? "/api/resource/dreamina-proxy-asset"
    : normalizeVideoProvider(provider) === VIDEO_PROVIDER_GUAVIDEO
      ? "/api/resource/guavideo-asset"
      : null
);

// Legacy Gua resource protocol only. Official 2.5 uses its catalog's
// supportsAssetIngest and /api/modelark/assets, never these entity-ID writebacks.
export const supportsVideoAssetIngest = (provider) => (
  [
    VIDEO_PROVIDER_GUAVIDEO,
    VIDEO_PROVIDER_ENG_GUAVIDEO,
    VIDEO_PROVIDER_OMINI_SEEDANCE,
  ].includes(normalizeVideoProvider(provider))
);

export const isSeedanceVideoProvider = (provider) => {
  const normalized = normalizeVideoProvider(provider);
  return normalized === VIDEO_PROVIDER_GUAVIDEO
    || normalized === VIDEO_PROVIDER_VOLCENGINE_SEEDANCE
    || normalized === VIDEO_PROVIDER_ENG_GUAVIDEO
    || normalized === VIDEO_PROVIDER_OMINI_SEEDANCE;
};

export const isDurableVideoPollingProvider = (provider) => {
  const normalized = normalizeVideoProvider(provider);
  if (normalized === VIDEO_PROVIDER_BYTEPLUS_LAS_SD25) return false;
  return normalized === VIDEO_PROVIDER_WANX
    || normalized === VIDEO_PROVIDER_WAND_VEGA
    || normalized === VIDEO_PROVIDER_VOLCENGINE_SEEDANCE
    || isSeedance25Provider(normalized);
};

export const supportsVideoTaskCancel = (provider) => (
  normalizeVideoProvider(provider) !== VIDEO_PROVIDER_WAND_VEGA
    && !isSeedance25Provider(normalizeVideoProvider(provider))
    && !isDomesticOfficialSeedance(provider)
);

export const isOverseasVideoProvider = (provider) => {
  const normalized = normalizeVideoProvider(provider);
  return normalized === VIDEO_PROVIDER_ENG_GUAVIDEO
    || normalized === VIDEO_PROVIDER_OMINI_SEEDANCE
    || normalized === VIDEO_PROVIDER_BYTEPLUS_SD25
    || normalized === VIDEO_PROVIDER_BYTEPLUS_LAS_SD25;
};

export const getVideoProviderLabel = (provider) => {
  const raw = String(provider || "").trim().toLowerCase();
  if (raw === "wanx") return "万相 3.0";
  if (raw === "wan3.0-video") return "万相 3.0 标准版";
  if (raw === "wan3.0-video-prime") return "万相 3.0 高速版";
  if (raw === VIDEO_PROVIDER_WAND_VEGA) return "Wand Vega";
  const normalized = normalizeVideoProvider(provider);
  if (normalized === VIDEO_PROVIDER_AKOOL_SD25) return "AKOOL 2.5";
  if (normalized === VIDEO_PROVIDER_VOLCENGINE_SEEDANCE) return 'Seedance 2.0（国内官方）';
  if (normalized === VIDEO_PROVIDER_ASTROFLOW_SD25) return "Astroflow 2.5";
  if (normalized === VIDEO_PROVIDER_BYTEPLUS_SD25) return "Dreamina 2.5（海外代理）";
  if (normalized === VIDEO_PROVIDER_VOLCENGINE_SD25) return "Seedance 2.5（国内官方）";
  if (normalized === VIDEO_PROVIDER_BYTEPLUS_LAS_SD25) return "Dreamina 2.5（历史 LAS，已停用）";
  if (normalized === VIDEO_PROVIDER_NEWAPI_SD25) return "New API 2.5";
  if (normalized === VIDEO_PROVIDER_OMINI_SEEDANCE) return "GuaOmini";
  if (normalized === VIDEO_PROVIDER_ENG_GUAVIDEO) return "EngGuaVideo";
  if (normalized === VIDEO_PROVIDER_GUAVIDEO) return "GuaVideo";
  return "未知视频服务";
};

export const getPublicVideoLabel = (value, fallback = "视频生成") => {
  const source = String(value || "").trim() ? value : fallback;
  const raw = String(source || "").trim();
  if (!raw) return "";
  const normalizedRaw = raw.toLowerCase();
  if (normalizedRaw === "wanx") return "万相 3.0";
  if (normalizedRaw === "wan3.0-video") return "万相 3.0 标准版";
  if (normalizedRaw === "wan3.0-video-prime") return "万相 3.0 高速版";
  const provider = normalizeVideoProvider(raw);
  if (provider) return getVideoProviderLabel(provider);
  const normalized = normalizedRaw;
  if (normalized.includes("seedance-2-0-fast")) return "2.0 Fast";
  if (normalized.includes("seedance-2-0-mini")) return "2.0 Mini";
  if (normalized.includes("seedance-2-0")) return "2.0 标准";
  if (normalized.includes("seedance")) return raw.replace(/seedance/gi, "Video");
  return raw;
};

export const GUA_OMINI_VIDEO_RESOLUTIONS = Object.freeze(["480p", "720p"]);

export const getGuaOminiVideoResolutions = () => [...GUA_OMINI_VIDEO_RESOLUTIONS];

export const getVideoResolutionsForProvider = (provider) => {
  const normalizedProvider = normalizeVideoProvider(provider);
  if (isDomesticOfficialSeedance(normalizedProvider)) return [...getDomesticSeedanceResolutions()];
  if (normalizedProvider === VIDEO_PROVIDER_WANX) return ["480P", "720P", "1080P"];
  if (normalizedProvider === VIDEO_PROVIDER_WAND_VEGA) return ["720P", "1080P", "2K", "4K"];
  if (normalizedProvider === VIDEO_PROVIDER_OMINI_SEEDANCE) return getGuaOminiVideoResolutions();
  return ["480p", "720p"];
};

export const normalizeVideoResolutionForProvider = (provider, resolution, fallback = "720p") => {
  const value = String(resolution || "").trim();
  const normalizedProvider = normalizeVideoProvider(provider);
  if (normalizedProvider !== VIDEO_PROVIDER_OMINI_SEEDANCE) {
    return value || fallback;
  }
  if (GUA_OMINI_VIDEO_RESOLUTIONS.includes(value)) return value;
  return GUA_OMINI_VIDEO_RESOLUTIONS.includes(fallback) ? fallback : GUA_OMINI_VIDEO_RESOLUTIONS[0];
};

export const supportsProjectVideoAssetIngest = (projectId, fallbackProvider = VIDEO_PROVIDER_GUAVIDEO) => (
  supportsVideoAssetIngest(getProjectVideoProvider(projectId, fallbackProvider))
);

export const getProjectVideoAssetApiBase = (projectId, fallbackProvider = VIDEO_PROVIDER_GUAVIDEO) => (
  getVideoAssetApiBase(getProjectVideoProvider(projectId, fallbackProvider))
);

export const getProjectDefaultVideoModel = (projectId, fast = false) => {
  const profile = getProjectRuntimeProfile(projectId);
  const provider = getProjectVideoProvider(projectId, "");
  if (!provider) return "";
  if (isSeedance25Provider(provider) || isSd25Project(profile)) {
    return VIDEO_MODEL_NEWAPI_SD25;
  }
  if (provider === VIDEO_PROVIDER_WANX || isWan3Project(profile)) {
    return fast ? VIDEO_MODEL_WAN3_PRIME : VIDEO_MODEL_WAN3;
  }
  if (provider === VIDEO_PROVIDER_ENG_GUAVIDEO || provider === VIDEO_PROVIDER_OMINI_SEEDANCE) {
    return fast ? "dreamina-seedance-2-0-fast" : "dreamina-seedance-2-0";
  }
  if (isDomesticOfficialSeedance(provider)) return DOMESTIC_SEEDANCE_MODELS[fast ? 1 : 0].id;
  return provider === VIDEO_PROVIDER_GUAVIDEO
    ? (fast ? "doubao-seedance-2-0-fast-260128" : "doubao-seedance-2-0-260128")
    : "";
};

export const normalizeProjectVideoRatio = (value, fallback = PROJECT_DEFAULT_VIDEO_RATIO) => {
  const text = String(value || "").trim();
  if (PROJECT_VIDEO_RATIOS.includes(text)) return text;
  return PROJECT_VIDEO_RATIOS.includes(fallback) ? fallback : PROJECT_DEFAULT_VIDEO_RATIO;
};

export const getProjectDefaultVideoRatio = (projectId, fallback = PROJECT_DEFAULT_VIDEO_RATIO) => {
  const profile = getProjectRuntimeProfile(projectId);
  return normalizeProjectVideoRatio(profile?.defaultVideoRatio || profile?.videoRatio || profile?.defaultAspectRatio, fallback);
};

export const getProjectVisualStyleMode = (projectId) => {
  const profile = getProjectRuntimeProfile(projectId);
  const mode = String(profile?.visualStyleMode || "").trim().toLowerCase();
  if (mode === PROJECT_VISUAL_STYLE_FIXED && profile?.visualStyle) {
    return PROJECT_VISUAL_STYLE_FIXED;
  }
  if (mode === PROJECT_VISUAL_STYLE_NONE) {
    return PROJECT_VISUAL_STYLE_NONE;
  }
  return PROJECT_VISUAL_STYLE_LEGACY;
};

export const resolveProjectStyleAnchorForTask = (projectId, requestedStyleAnchor) => {
  const profile = getProjectRuntimeProfile(projectId);
  const mode = String(profile?.visualStyleMode || "").trim().toLowerCase();
  if (mode === PROJECT_VISUAL_STYLE_FIXED && profile?.visualStyle) {
    return normalizeVisualStyleKey(profile.visualStyle);
  }
  if (mode === PROJECT_VISUAL_STYLE_NONE) {
    return "custom";
  }
  return normalizeVisualStyleKey(requestedStyleAnchor);
};

export const getProjectMarketLabel = (projectId) => {
  const profile = getProjectRuntimeProfile(projectId);
  const provider = getProjectVideoProvider(projectId);
  if (isSeedance25Provider(provider)) {
    return { label: "2.5 项目", provider: getVideoProviderLabel(provider), color: "var(--color-accent-primary)" };
  }
  if (provider === VIDEO_PROVIDER_WANX || isWan3Project(profile)) {
    return { label: "Wan3.0 项目", provider: "万相 3.0", color: "var(--color-accent-primary)" };
  }
  if (provider === VIDEO_PROVIDER_OMINI_SEEDANCE) {
    return { label: "出海项目", provider: "GuaOmini", color: "var(--color-info, #38bdf8)" };
  }
  if (isDomesticOfficialSeedance(provider)) return { label: '国内项目', provider: 'Seedance 2.0（官方）', color: 'var(--color-accent-secondary)' };
  if (!provider) {
    return { label: "项目配置未加载", provider: "未配置", color: "var(--color-warning)" };
  }
  return isOverseasProjectProfile(profile)
    ? { label: "出海项目", provider: "EngGuaVideo", color: "var(--color-success)" }
    : { label: "国内项目", provider: "GuaVideo", color: "var(--color-accent-secondary)" };
};
